import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useSummary } from 'hooks/useSummary';
import styled from 'styled-components';
import { INLINES } from '@contentful/rich-text-types';
import { TrackedLink } from 'components/common/TrackedLink';
import {
  parseStatutePath,
  StatuteLink,
  StatuteLinkProps,
} from 'features/link-helpers/StatuteLink';
import {
  parseCasePath,
  CaseLink,
  CaseLinkProps,
} from 'features/link-helpers/CaseLink';
import {
  parseCourtRulePath,
  CourtRuleLink,
  CourtRuleLinkProps,
} from 'features/link-helpers/CourtRuleLink';
import {
  parseNewsPath,
  NewsLink,
  NewsLinkProps,
} from 'features/link-helpers/NewsLink';
import {
  parseSecondarySourcePath,
  SecondarySourceLink,
  SecondarySourceLinkProps,
} from 'features/link-helpers/SecondarySourceLink';
import {
  parsePractitionerPath,
  PractitionerLink,
  PractitionerLinkProps,
} from 'features/link-helpers/PractitionerLink';
import {
  parseCoursePath,
  CourseLink,
  CourseLinkProps,
} from 'features/link-helpers/CourseLink';
import {
  parseCollectionPath,
  CollectionLink,
  CollectionLinkProps,
} from 'features/link-helpers/CollectionLink';

// Main URL parsing function
function parseInternalUrl(uri: string) {
  // Remove workspace prefix if present
  let path = uri;
  if (path.startsWith('/workspaces/')) {
    const parts = path.split('/');
    if (parts.length >= 3) {
      path = '/' + parts.slice(3).join('/');
    }
  }

  // Remove research.ceb.com domain if present
  path = path.replace(/^https?:\/\/research\.ceb\.com/, '');

  // Try parsing with each parser
  const parsers = [
    { parser: parseStatutePath, type: 'statute' as const },
    { parser: parseCasePath, type: 'case' as const },
    { parser: parseCourtRulePath, type: 'courtRule' as const },
    { parser: parseNewsPath, type: 'news' as const },
    { parser: parseSecondarySourcePath, type: 'secondarySource' as const },
    { parser: parsePractitionerPath, type: 'practitioner' as const },
    { parser: parseCoursePath, type: 'course' as const },
    { parser: parseCollectionPath, type: 'collection' as const },
  ];

  for (const { parser, type } of parsers) {
    const result = parser(path);
    if (result) {
      return { type, props: result };
    }
  }

  return null;
}

const INLINE_INTERNAL_LINK_RENDERNODE = {
  [INLINES.HYPERLINK]: (node: any) => {
    const isInternal =
      node.data.uri.match(/research\.ceb\.com/) ||
      node.data.uri.startsWith('/');

    if (!isInternal) {
      return (
        <a href={node.data.uri}>
          <ContentfulContent content={{ json: { content: node.content } }} />
        </a>
      );
    }

    // Try to parse the URL and use appropriate link helper
    const parsedUrl = parseInternalUrl(node.data.uri);

    if (parsedUrl) {
      // Use appropriate link helper component based on type
      switch (parsedUrl.type) {
        case 'statute': {
          return (
            <StatuteLink {...(parsedUrl.props as StatuteLinkProps)}>
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </StatuteLink>
          );
        }
        case 'case': {
          return (
            <CaseLink {...(parsedUrl.props as CaseLinkProps)}>
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </CaseLink>
          );
        }
        case 'courtRule': {
          return (
            <CourtRuleLink {...(parsedUrl.props as CourtRuleLinkProps)}>
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </CourtRuleLink>
          );
        }
        case 'news': {
          return (
            <NewsLink {...(parsedUrl.props as NewsLinkProps)}>
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </NewsLink>
          );
        }
        case 'secondarySource': {
          return (
            <SecondarySourceLink
              {...(parsedUrl.props as SecondarySourceLinkProps)}
            >
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </SecondarySourceLink>
          );
        }
        case 'practitioner': {
          return (
            <PractitionerLink {...(parsedUrl.props as PractitionerLinkProps)}>
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </PractitionerLink>
          );
        }
        case 'course': {
          return (
            <CourseLink {...(parsedUrl.props as CourseLinkProps)}>
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </CourseLink>
          );
        }
        case 'collection': {
          return (
            <CollectionLink {...(parsedUrl.props as CollectionLinkProps)}>
              {node?.content && (
                <ContentfulContent
                  content={{ json: { content: node.content } }}
                />
              )}
            </CollectionLink>
          );
        }
      }
    }

    // Fallback to TrackedLink for internal links that couldn't be parsed
    return (
      <TrackedLink
        to={
          node.data.uri.startsWith('/')
            ? node.data.uri
            : node.data.uri.replace(/https*:\/\/research.ceb.com/, '')
        }
      >
        {node?.content && (
          <ContentfulContent content={{ json: { content: node.content } }} />
        )}
      </TrackedLink>
    );
  },
};

interface ContentfulContentProps {
  renderNodeOptions?: any;
  document?: any;
  content?: any;
}

export function ContentfulContent({
  renderNodeOptions,
  document,
  content,
}: ContentfulContentProps) {
  const contentfulDocument = document || {
    nodeType: 'document',
    data: {},
    content: content.json.content,
  };

  const options = React.useMemo(() => {
    return {
      renderNode: {
        ...INLINE_INTERNAL_LINK_RENDERNODE,
        ...(renderNodeOptions || {}),
      },
    };
  }, [renderNodeOptions]);

  return documentToReactComponents(contentfulDocument, options);
}

interface ContentfulSummaryProps {
  summaryLength?: number;
  children?: React.ReactNode;
  document?: any;
  content?: any;
}

export const ContentfulSummary = ({
  summaryLength = 300,
  children,
  document,
  content,
}: ContentfulSummaryProps) => {
  const contentFul = React.useRef(null);
  const [summary] = useSummary(contentFul, 'div > p', summaryLength);
  return (
    <>
      <StyledContentfulSummary className="contentful" ref={contentFul}>
        <ContentfulContent document={document} content={content} />
        {children}
      </StyledContentfulSummary>
      {summary ? (
        <StyledContentfulSummary className="contentful-summary">
          <p>{summary}...</p>
          {children}
        </StyledContentfulSummary>
      ) : null}
    </>
  );
};

interface RawContentfulSummaryProps {
  summaryLength?: number;
  children?: React.ReactNode;
  document?: any;
  content?: any;
}

export const RawContentfulSummary = ({
  summaryLength = 300,
  children,
  document,
  content,
}: RawContentfulSummaryProps) => {
  const contentFul = React.useRef(null);
  const [summary] = useSummary(contentFul, 'div > p', summaryLength);
  return (
    <>
      <StyledContentfulSummary className="contentful" ref={contentFul}>
        <ContentfulContent document={document} content={content} />
        {children}
      </StyledContentfulSummary>
      {summary ? summary : null}
    </>
  );
};

const StyledContentfulSummary = styled.div`
  &.contentful {
    display: none;
  }
`;
